import { FC } from 'react'
import DOMPurify from 'dompurify'

import cn from '@/common/cn'

import { IConversation } from '@/context/types'
import { CONVERSATION_STATE, USER_TYPES } from '@/context/constants'
import { formatConversationDateRelative } from '@/common/utils'
import useMessenger from '@/hooks/useMessenger'
import { StoreContext } from '@/context/context'
import { getAgentInitials } from '../ContentTypes/utils'

export interface ConversationProps {
  conversation: IConversation
  handleClick: Function
  highlightedClassName?: string
  className?: string
}

const Conversation: FC<ConversationProps> = ({
  conversation,
  handleClick,
  highlightedClassName,
  className,
}) => {
  const {
    getSelectedConversation,
    getConfig,
    getInputMessage,
    getLastConversationMessage,
  } = useMessenger(StoreContext)
  const config = getConfig()
  const activeClassName = highlightedClassName ?? 'pmui-bg-orange-50'

  const getDraftMessage = () => {
    const inputMessage = getInputMessage(conversation.id)
    const sanitized = DOMPurify.sanitize(inputMessage, {
      ALLOWED_TAGS: ['b', 'strong'],
      ALLOWED_ATTR: ['style'],
    })

    if (!sanitized) {
      return null
    }

    return `<span class="pmui-text-red-600">draft:</span> ${sanitized}`
  }

  const getLastConversationMessageText = () => {
    const sanitized = DOMPurify.sanitize(
      conversation?.metadata?.lastMessage?.content?.text || '...',
      { ALLOWED_TAGS: ['b', 'strong'], ALLOWED_ATTR: ['style'] },
    )

    return sanitized
  }

  const getAgentAssigned = (conversation: IConversation) => {
    if (!conversation?.metadata?.agentAssigned) {
      return (
        <div className="pmui-w-10 pmui-h-10 pmui-rounded-full pmui-flex pmui-justify-center pmui-items-center">
          <span className="icon icon-ic-poplar pmui-text-lg" />
        </div>
      )
    }

    const splitName = conversation?.metadata.agentAssigned?.split(' ')

    return getAgentInitials(splitName[0], splitName[splitName.length - 1])
  }

  return (
    <div className={cn('pmui-w-full pmui-px-4', className)}>
      <button
        className={cn(
          'pmui-px-4 pmui-py-4 pmui-flex pmui-items-center pmui-rounded-2xl pmui-w-full',
          'hover:pmui-cursor-pointer focus:pmui-outline-none',
          'pmui-transition pmui-duration-500 pmui-ease-out',
          `hover:${activeClassName}`,
          {
            [activeClassName]: getSelectedConversation() === conversation.id,
          },
        )}
        onClick={() => handleClick(conversation.id)}
      >
        <div className="pmui-flex pmui-items-center pmui-justify-between pmui-w-full">
          <div
            className={cn(
              'pmui-inline-flex pmui-justify-center pmui-items-center pmui-overflow-hidden',
              'md:pmui-w-10 md:pmui-h-10',
              'pmui-w-8 pmui-h-8 pmui-mr-4',
              'pmui-rounded-full pmui-bg-gray-600 avatar pmui-outline-none',
            )}
          >
            <span className="sm:pmui-text-sm md:pmui-text-base pmui-text-white pmui-font-semibold">
              {getAgentAssigned(conversation)}
            </span>
          </div>
          <div className="pmui-w-full pmui-min-w-0">
            <div className="pmui-flex pmui-items-center pmui-justify-between pmui-mb-2">
              <div
                className={cn(
                  'pmui-text-sm pmui-font-semibold pmui-text-pop-gray',
                  'pmui-overflow-hidden pmui-text-ellipsis pmui-whitespace-nowrap',
                  {
                    'pmui-opacity-70':
                      conversation.metadata?.isRead ||
                      conversation.metadata?.isClosed ||
                      conversation.metadata?.state ===
                        CONVERSATION_STATE.CLOSED,
                  },
                )}
              >
                {config?.userType === USER_TYPES.VENDOR &&
                conversation.metadata?.workOrders
                  ? conversation.metadata?.workOrders
                      .map((wo: any) => wo.number)
                      .join(', ')
                  : conversation.displayName}
              </div>
              <div
                className={cn(
                  'pmui-ml-3 pmui-text-xs pmui-min-w-max pmui-text-gray-600',
                  {
                    'pmui-opacity-70':
                      conversation.metadata?.isRead ||
                      conversation.metadata?.isClosed ||
                      conversation.metadata?.state ===
                        CONVERSATION_STATE.CLOSED,
                  },
                )}
              >
                {formatConversationDateRelative(
                  getLastConversationMessage(conversation.id)?.received ||
                    conversation.lastUpdatedAt,
                )}
              </div>
            </div>
            <div
              className={cn(
                'pmui-text-start pmui-text-sm ',
                'pmui-overflow-hidden pmui-w-full pmui-text-ellipsis pmui-whitespace-nowrap',
                {
                  'pmui-opacity-70':
                    conversation.metadata?.isRead ||
                    conversation.metadata?.isClosed ||
                    conversation.metadata?.state === CONVERSATION_STATE.CLOSED,
                },
              )}
            >
              <div
                className="pmui-w-full pmui-text-ellipsis pmui-whitespace-nowrap pmui-overflow-hidden"
                dangerouslySetInnerHTML={{
                  __html: getDraftMessage() || getLastConversationMessageText(),
                }}
              />
            </div>
          </div>
        </div>
      </button>
    </div>
  )
}

export default Conversation
